import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';

import { ButtonCancel } from '../../../components/general/mini_components';
import { SnackbarTypeSeverity } from '../../../constants/types';
import { cancelRequestService } from '../../../services/request';
import ReasonModal from './ReasonModal';
import { canCancelRequest } from './requestModification';
import { IRequest } from './types';
import { useRequestMutation } from './useRequestMutation';

export interface CancelRequestActionCoreProps {
    request: IRequest;
    permissions: string[];
    loading: boolean;
    onConfirm: (reason: string) => void | Promise<boolean>;
}

// Presentational core: shows the cancel button + mandatory-reason modal when the
// policy allows it (canCancelRequest), otherwise renders nothing.
export const CancelRequestActionCore: React.FC<CancelRequestActionCoreProps> = ({ request, permissions, loading, onConfirm }) => {
    const [open, setOpen] = useState(false);

    if (!canCancelRequest(request, permissions)) {
        return null;
    }

    return (
        <>
            <ReasonModal
                open={open}
                closeModal={() => setOpen(false)}
                onConfirm={onConfirm}
                loading={loading}
                titleId="pages.hospital.services.request.cancel.title"
                bodyId="pages.hospital.services.request.cancel.modal"
                reasonLabelId="pages.hospital.services.request.cancel.reason_label"
                reasonTestId="cancel-reason"
            />
            <ButtonCancel onClick={() => setOpen(true)} data-testid="cancel-request">
                <Translate id="pages.hospital.services.request.cancel.button" />
            </ButtonCancel>
        </>
    );
};

interface CancelRequestActionProps {
    request: IRequest;
    uuidInvestigation: string;
    permissions: string[];
    onCancelled: (updated: IRequest) => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

// Container: wires the cancel service to the presentational core.
const CancelRequestAction: React.FC<CancelRequestActionProps> = ({ request, uuidInvestigation, permissions, onCancelled, setShowSnackbar }) => {
    const { loading, run } = useRequestMutation(setShowSnackbar, onCancelled);

    const handleConfirm = (reason: string) =>
        run(() => cancelRequestService(uuidInvestigation, request.id, reason), 'pages.hospital.services.request.cancel.success');

    return (
        <CancelRequestActionCore
            request={request}
            permissions={permissions}
            loading={loading}
            onConfirm={handleConfirm}
        />
    );
};

export default CancelRequestAction;
